import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ChevronDown, ChevronRight, Phone, MessageCircle, User, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useSite, whatsappLink } from "@/context/SiteContext";

const SERVICE_LINKS = [
  { to: "/services", label: "All Services" },
  { to: "/services/post-construction-cleaning", label: "Post-Construction Detail" },
  { to: "/services/move-in-move-out-cleaning", label: "Move-In / Move-Out" },
  { to: "/services/general-deep-cleaning", label: "Deep Reset" },
  { to: "/services/property-detailing", label: "Private Residence Detail" },
  { to: "/detailing-menu", label: "Focused Detail Menu" },
];
const PLAN_LINKS = [
  { to: "/estimate", label: "Instant Estimate" },
  { to: "/request-time", label: "Book a Time" },
  { to: "/packages", label: "Packages" },
  { to: "/ask", label: "Ask Ascendia" },
];
const ABOUT_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/ascendia-standard", label: "The Ascendia Standard" },
  { to: "/what-we-use", label: "What We Use" },
  { to: "/trust", label: "Trust Center" },
  { to: "/reviews", label: "Reviews" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "The Ascendia Guide" },
];

const Dropdown = ({ label, items, groupLabel, groupItems }) => {
  const [open, setOpen] = useState(false);
  const renderLink = (it) => (
    <Link key={it.to} to={it.to} className="group flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-white/80 hover:bg-white/[0.06] hover:text-white transition-colors">
      <span>{it.label}</span>
      <ChevronRight className="h-3.5 w-3.5 text-gold-soft opacity-0 -translate-x-1 transition-[opacity,transform] duration-200 ease-[var(--ease-out)] group-hover:opacity-100 group-hover:translate-x-0" />
    </Link>
  );
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 text-sm text-white/85 hover:text-white transition-colors py-2">
        {label} <ChevronDown className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-3 w-64 z-50">
          <div className="card-accent-top rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--bg-2)]/95 backdrop-blur-md p-2 shadow-[var(--shadow-3)]">
            {items.map(renderLink)}
            {groupItems && groupItems.length > 0 && (
              <>
                <div className="gold-hairline my-2" />
                <p className="px-3 pb-1 pt-1 text-[10px] uppercase tracking-[0.18em] text-gold-soft">{groupLabel}</p>
                {groupItems.map(renderLink)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const navLinkCls = ({ isActive }) =>
  `relative text-sm transition-colors duration-150 py-2 ${isActive ? "text-gold-soft after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full after:bg-gold/60" : "text-white/80 hover:text-white"}`;

const ACCOUNT_LINKS = [
  { to: "/account", label: "Dashboard" },
  { to: "/account/properties", label: "Properties" },
  { to: "/account/quotes", label: "Quotes" },
  { to: "/account/bookings", label: "Bookings" },
  { to: "/account/rewards", label: "Rewards" },
  { to: "/account/profile", label: "Profile" },
];

const AccountMenu = ({ client, onLogout }) => {
  const [open, setOpen] = useState(false);
  if (!client) {
    return (
      <Link to="/account/login" data-testid="nav-client-login" className="inline-flex items-center gap-2 rounded-xl border border-gold/60 bg-gold/10 px-3.5 py-2 text-sm font-medium text-gold-soft hover:bg-gold/20 hover:text-gold transition-colors whitespace-nowrap">
        <User className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">Client Login</span>
      </Link>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to="/account" data-testid="nav-my-ascendia" className="inline-flex items-center gap-1.5 rounded-xl border border-gold/60 bg-gold/10 px-3.5 py-2 text-sm font-medium text-gold-soft hover:bg-gold/20 hover:text-gold transition-colors whitespace-nowrap">
        <User className="h-4 w-4" /> <span className="hidden xs:inline sm:inline">My Ascendia</span> <ChevronDown className="hidden sm:inline h-4 w-4" />
      </Link>
      {open && (
        <div className="absolute right-0 top-full pt-3 w-56 z-50 hidden sm:block">
          <div className="card-accent-top rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--bg-2)]/95 backdrop-blur-md p-2 shadow-[var(--shadow-3)]">
            <p className="px-3 py-1.5 text-[11px] text-muted-c truncate">{client.first_name} {client.last_name}</p>
            <div className="gold-hairline my-1" />
            {ACCOUNT_LINKS.map((it) => (
              <Link key={it.to} to={it.to} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-white/80 hover:bg-white/[0.06] hover:text-white transition-colors">{it.label}<ChevronRight className="h-3.5 w-3.5 text-gold-soft" /></Link>
            ))}
            <div className="gold-hairline my-1" />
            <button onClick={onLogout} data-testid="nav-client-logout" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/80 hover:bg-white/[0.06] hover:text-white"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const site = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [client, setClient] = useState(null);
  const loc = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setMobileOpen(false), [loc.pathname]);
  const refreshClient = React.useCallback(() => {
    api.get("/client/me").then(({ data }) => setClient(data)).catch(() => setClient(null));
  }, []);
  useEffect(() => { refreshClient(); }, [loc.pathname, refreshClient]);
  useEffect(() => {
    // Keep header auth state fresh across login/logout and tab focus.
    const onAuth = () => refreshClient();
    window.addEventListener("ascendia-auth-change", onAuth);
    window.addEventListener("focus", onAuth);
    return () => { window.removeEventListener("ascendia-auth-change", onAuth); window.removeEventListener("focus", onAuth); };
  }, [refreshClient]);
  const clientLogout = async () => {
    try { await api.post("/client/logout"); } catch (e) { console.error("client logout failed", e); }
    setClient(null);
    window.location.href = "/";
  };
  const mLink = (to) =>
    `min-h-[44px] flex items-center rounded-lg px-3 text-sm transition-colors ${loc.pathname === to ? "text-white bg-white/[0.06] border border-[color:var(--border-subtle)]" : "text-white/80 hover:bg-white/5"}`;

  return (
    <header
      data-testid="site-header-nav"
      className={`sticky top-0 z-50 transition-colors ${scrolled ? "bg-[#0B0B0C]/90 backdrop-blur-md border-b border-[color:var(--border-subtle)]" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6 lg:px-8 flex h-[72px] items-center justify-between gap-4">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2.5" data-testid="nav-logo-link">
          <img src={site.logo} alt="Ascendia Detail" className="h-9 w-9 object-contain" />
          <span className="font-display text-lg font-semibold tracking-tight text-white whitespace-nowrap">Ascendia<span className="text-gold"> Detail</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <Dropdown label="Services" items={SERVICE_LINKS} groupLabel="Plan & Book" groupItems={PLAN_LINKS} />
          <NavLink to="/property-intelligence" className={({ isActive }) => `${navLinkCls({ isActive })} text-gold-soft hover:text-gold`}>Property Intelligence</NavLink>
          <NavLink to="/gallery" className={navLinkCls}>Before &amp; After</NavLink>
          <NavLink to="/for-professionals" className={navLinkCls}>For Professionals</NavLink>
          <Dropdown label="About" items={ABOUT_LINKS} />
        </nav>

        <div className="flex flex-shrink-0 items-center gap-3">
          <a href={`tel:${site.phone}`} className="hidden xl:flex items-center gap-2 text-sm text-white/80 hover:text-white whitespace-nowrap">
            <Phone className="h-4 w-4 text-gold-soft" /> {site.phone}
          </a>
          <AccountMenu client={client} onLogout={clientLogout} />
          <Link to="/contact" data-testid="nav-request-quote-button" className="hidden sm:inline-flex rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft transition-colors">
            Request a Quote
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-open-button" className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[color:var(--border-subtle)] text-white">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] max-w-sm border-l border-[color:var(--border-subtle)] bg-[#0B0B0C] text-white p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Browse services, plan and book, and company links.</SheetDescription>
              <div className="flex items-center gap-2.5 px-4 h-[72px] border-b border-[color:var(--border-subtle)] flex-shrink-0">
                <img src={site.logo} alt="Ascendia Detail" className="h-8 w-8 object-contain" />
                <span className="font-display font-semibold text-white">Ascendia <span className="text-gold">Detail</span></span>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1">
                <Link to="/property-intelligence" className={`${mLink("/property-intelligence")} text-gold-soft`}>Property Intelligence™</Link>
                <div className="gold-hairline my-3" />
                <p className="kicker mb-1">Services</p>
                {SERVICE_LINKS.map((it) => (
                  <Link key={it.to} to={it.to} className={mLink(it.to)}>{it.label}</Link>
                ))}
                <div className="gold-hairline my-3" />
                <p className="kicker mb-1">Plan &amp; Book</p>
                {PLAN_LINKS.map((it) => (
                  <Link key={it.to} to={it.to} className={mLink(it.to)}>{it.label}</Link>
                ))}
                <Link to="/gallery" className={mLink("/gallery")}>Before &amp; After</Link>
                <Link to="/for-professionals" className={mLink("/for-professionals")}>For Professionals</Link>
                <div className="gold-hairline my-3" />
                <p className="kicker mb-1">My Ascendia</p>
                {client ? (<>
                  {ACCOUNT_LINKS.map((it) => (<Link key={it.to} to={it.to} className={mLink(it.to)}>{it.label}</Link>))}
                  <button onClick={clientLogout} data-testid="mobile-client-logout" className={`${mLink("/logout")} w-full text-left`}>Log out</button>
                </>) : (
                  <Link to="/account/login" data-testid="mobile-client-login" className={mLink("/account/login")}>Client Login / Sign up</Link>
                )}
                <div className="gold-hairline my-3" />
                <p className="kicker mb-1">Company</p>
                {ABOUT_LINKS.map((it) => (
                  <Link key={it.to} to={it.to} className={mLink(it.to)}>{it.label}</Link>
                ))}
              </div>
              <div className="flex-shrink-0 border-t border-[color:var(--border-subtle)] bg-[#0B0B0C]/95 backdrop-blur p-3 grid grid-cols-3 gap-2">
                <a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp-button" className="inline-flex flex-col items-center justify-center gap-1 rounded-lg border border-[color:var(--border-neutral)] bg-white/[0.03] py-2.5 text-xs text-white hover:bg-white/[0.06]"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
                <a href={`tel:${site.phone}`} data-testid="mobile-menu-call-button" className="inline-flex flex-col items-center justify-center gap-1 rounded-lg border border-[color:var(--border-neutral)] bg-white/[0.03] py-2.5 text-xs text-white hover:bg-white/[0.06]"><Phone className="h-4 w-4 text-gold-soft" /> Call</a>
                <Link to="/contact" data-testid="mobile-menu-request-quote-button" className="inline-flex flex-col items-center justify-center gap-1 rounded-lg bg-gold py-2.5 text-xs font-semibold text-black hover:bg-gold-soft">Get Quote</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
